import { MIN_EVIDENCE_FOR_ACCUSATION } from "@cold-case/shared/constants";

/**
 * Full in-game Help — semantic HTML only (styles from #player-manual-overlay in index.html).
 */
export function buildPlayerManualHtml(): string {
  const minEv = MIN_EVIDENCE_FOR_ACCUSATION;
  return `
<article class="ccc-manual">
  <p class="manual-lead">
    Welcome, detective. This guide covers everything you need to enjoy <strong>Cold Case Solvers</strong> at your own pace.
    Take a breath, read a section at a time, and dip back in whenever something new appears on screen.
  </p>

  <h2>What you are doing here</h2>
  <p>
    You are solving a procedurally written mystery with friends or solo. The city is split into two neighborhoods at night:
    a busier <strong>downtown</strong> district and the quieter <strong>Maple Heights</strong> suburb.
    Your job is to explore, gather testimony and physical leads, and decide who is responsible when you are ready to accuse.
  </p>
  <p>
    The tone is meant to be approachable: think cooperative board-game night energy, not a grim homework assignment.
    Share what you find, laugh at wrong turns, and celebrate when a clue finally clicks.
  </p>

  <h2>Moving around</h2>
  <p>
    <strong>Tap or click</strong> empty street to walk there. Use <strong>WASD</strong> or the
    <strong>arrow keys</strong> for eight-direction movement (including diagonals). You stay on streets and paths — building sides are fenced off; only approach arrows and road-facing paths let you reach a story building.
  </p>
  <p>
    The camera follows you. If you play with others, you will each have your own character; stay in voice or text chat
    and call out interesting spots — two pairs of eyes beat one.
  </p>
  <p>
    If you stand still for about ten seconds with no menus open, an <strong>Unstuck</strong> link appears in the <strong>left HUD</strong>, directly under the radio icon.
    Tap it to jump to the zone travel arrow on the main street (downtown or Maple Heights, whichever you are in).
  </p>

  <h2>Following a partner (multiplayer)</h2>
  <p>
    With two or three detectives in a session, <strong>tap another player&apos;s character</strong> on the street to ask whether you want to follow them.
    If the game cannot start follow (wrong zone, arrest phase, already following, and so on), a short message appears at the top of the screen for a few seconds — it is not posted to the radio.
    If you choose <strong>Yes</strong>, you tag along as a spectator: your detective trails theirs, you see their camera view, and their Case File tabs and interviews open on your screen in <strong>read-only</strong> form (buttons are greyed out).
  </p>
  <p>
    While following, you cannot tap buildings, witnesses, or map objects yourself; the leader&apos;s investigations and dialogs mirror to you automatically. If they open the <strong>radio</strong> or <strong>Case file</strong>, you see the same overlay in read-only form (including <strong>Radio Comms</strong> and <strong>Dispatch Log</strong> tabs). On mirrored dialogs, use the green <strong>Nudge</strong> button to tell the leader you are caught up and they can continue.
    The red <strong>Stop Following</strong> button stays on screen while you trail your partner, but it hides during a mirrored interview (same as the other HUD shortcuts) so it does not cover the dialog — use <strong>Nudge</strong> inside the dialog instead.
    Starting an accusation ends follow mode for everyone immediately.
  </p>

  <h2>Talking to people</h2>
  <p>
    When you stand near someone who lives in the case world, a hint appears at the bottom of the screen.
    <strong>Tap them</strong> (or press <kbd>E</kbd>) to open <strong>case notes</strong> on that person — their name and a short read on who they are. Tap <strong>Begin interview</strong> when you are ready. They speak first; tap <strong>I have some questions</strong>, then choose one topic at a time. Conversations may reveal new angles on the story, and sometimes a <strong>clue</strong> drops straight into your journal.
  </p>
  <p>
    Read dialogue slowly the first time; names and small details often matter later when you compare stories with your team.
  </p>

  <h2>Searching buildings</h2>
  <p>
    Along the sidewalk in front of each story building, look for an <strong>up arrow</strong> on the ground: <strong>green</strong> means you can enter; <strong>red</strong> means you are blocked until you earn clearance (tap the arrow or press <kbd>E</kbd> beside it to read why).
    Use the <strong>arrow only</strong> — tap it or press <kbd>E</kbd> when you are beside it and the HUD says <strong>Enter</strong> or <strong>Can't enter</strong>. Tapping the building façade does nothing.
    You walk around inside on a small floor plan; exits are on the <strong>left or right wall</strong> only (red chevrons). The top-right label shows the building name (handy for radio callouts).
  </p>
  <p>
    A <strong>red chevron on the floor</strong> points to the exit — tap it or press <kbd>E</kbd> nearby to return to the street in front of the building (you will be asked to confirm before leaving).
    Some interiors are empty; others hide a <strong>neon yellow triangle</strong> on the ground (examine with <kbd>E</kbd>) or a <strong>witness</strong> you can interview inside.
  </p>
  <p>
    The crime scene itself is special — treat it with extra attention, but do not ignore side streets; red herrings and background color both exist on purpose.
  </p>
  <p>
    Some locations are <strong>permission-gated</strong>: a <strong>red</strong> entry arrow stays red until <em>you</em> have interviewed the right person of interest and made progress in their dialogue (ask at least one question or learn something new). Tap the arrow to read the locked-building message — it is not repeated in the bottom HUD. In duo play, your partner clearing a building does not clear it for you — get your own clearance, then return when the arrow turns <strong>green</strong> and choose <strong>Enter</strong> to go inside.
  </p>

  <h2>Your journal</h2>
  <p>
    Press <kbd>J</kbd> or tap <strong>Case file</strong> on the left under the investigation clock for the case brief; press <kbd>M</kbd> or tap <strong>Map</strong> for the zone map. It is your shared case file: map, briefings, clues, people of interest, and team notes.
  </p>
  <ul>
    <li><strong>Map</strong> — schematic of the current zone with sorted <strong>Buildings</strong> and <strong>Witnesses</strong> lists (collapse either section; your leader’s list state syncs if you are following). Undiscovered places show <strong>???</strong> until you enter a building or open case notes on someone; the type and zone still appear beside each entry. Undiscovered rows look faded; the crime scene building is always named, italic with “(Crime scene)” below. Click any entry or map dot for a waypoint (yours is gold; teammates’ are blue). On the map, <strong>blue</strong> is you and <strong>purple</strong> is a teammate; <em>inside bldg</em> appears when someone is indoors.</li>
    <li><strong>Brief</strong> — case title, victim summary, crime-scene hook, and plain-language guidance on when you can use <strong>Accuse</strong>.</li>
    <li><strong>Timeline</strong> — victim time of death, where they were found, and backstory text.</li>
    <li><strong>Clues</strong> — discovered clues with titles and descriptions, plus <strong>triangulation</strong> when the case defines pairs you can confirm after you hold both source clues.</li>
    <li><strong>Suspects</strong> — people you have actually interviewed (case notes or dialog), with player-facing roles only (never story spoilers). Others appear only as a count under “Not yet interviewed.” Flag or unflag anyone you have met.</li>
    <li><strong>Notes</strong> — shared team feed: everyone sees who posted what and how long ago (relative to the HUD investigation clock). Add a note and it syncs for the whole group.</li>
    <li><strong>Accuse</strong> — once you have enough discovered clues (often ${minEv}), tap a portrait to name the killer and request a warrant. Use <strong>✕</strong> to rule someone out for the team.</li>
  </ul>
  <p>
    Close Case Files with the on-screen button or <kbd>Escape</kbd> when you are done reading.
  </p>

  <h2>Changing neighborhoods (transit)</h2>
  <p>
    Each zone has a highlighted transit marker (soft yellow arrow at the end of the main street). Stand near it and press <kbd>E</kbd>, or tap the arrow, to travel to the other neighborhood — you will be asked to confirm before you leave. The camera eases in so the hop feels less disorienting.
    Watch the zone label at the top of the screen — it helps you stay oriented when you move between downtown and Maple Heights. From downtown, follow the road to the <strong>north-west</strong> end and use the glowing arrow (tap it or press <kbd>E</kbd>) to travel to Maple Heights.
  </p>

  <h2>Radio</h2>
  <p>
    Tap the radio icon on the left HUD to open a full-screen overlay with two tabs:
    <strong>Radio Comms</strong> for teammate chat (type a message, then <strong>Send</strong> or <kbd>Enter</kbd>),
    and <strong>Dispatch Log</strong> for system lines — clue discoveries, building clearance grants, and similar alerts.
    A pulsing halo on the radio button means unread dispatch traffic.
    Tap <strong>Close</strong> or press <kbd>Escape</kbd> when you are done. Use comms to coordinate:
    “I am on the porch with a new clue,” “Meet at transit,” or “I think we should revisit the store.”
  </p>
  <p>
    Keep messages kind and clear; everyone is here to have fun together.
  </p>

  <h2>Multiplayer tips</h2>
  <p>
    While you wait in the lobby before the host starts, use <strong>Display name</strong> to set how you appear on your detective card, in lobby chat, and on in-game radio messages.
    Guests start as <strong>Guest #1</strong>, <strong>Guest #2</strong>, and so on; registered players see their preferred name by default. You can change it anytime before the case begins.
  </p>
  <p>
    In duo mode, split the map intentionally: one person chases alibis while another re-checks physical evidence.
    Cross-reference journal tabs out loud — you might realize two clues only make sense when combined.
  </p>
  <p>
    If someone is newer to games like this, invite them to drive the journal while you explore, or swap roles each session.
  </p>

  <h2>Investigation time and accusation</h2>
  <p>
    The HUD clock counts <strong>up</strong> from <strong>0:00</strong> when your investigation begins (not while you wait in the lobby).
    It tracks how long you have been on the case. In <strong>solo</strong> play, the clock pauses while you read dialogue or building text; in <strong>duo</strong> play, the shared clock keeps running — only your character stops to read.
    There is no hard deadline — accuse when your team is ready. You win or lose based on whether you name the right suspect and the judge signs the warrant.
  </p>
  <p>
    When you have collected enough <strong>discovered</strong> clues (the case’s minimum, often ${minEv}), open the <strong>Accuse</strong> tab.
    In <strong>solo</strong> play, tap a suspect and request a warrant.
    In <strong>duo</strong> play, tap <strong>Start an accusation</strong> — everyone votes Yes or No (both must agree).
    If the vote passes, a shared accusation screen opens with step-by-step instructions: review clues, each choose a suspect, align on one person in <em>Make an Arrest</em>, then everyone taps <strong>Agree to Request Warrant</strong>.
    You have 3 minutes in that phase; if you do not all lock in, the case is lost (same as a wrong accusation) and your team returns to the lobby.
  </p>
  <p>
    Mistakes are part of detective fiction. If the result is not what you hoped, laugh it off and queue another case.
  </p>

  <h2>Mobile and reconnecting</h2>
  <p>
  If you switch to another app or lock your phone, your detective stays in the case for a short while.
  When you return, the game reconnects automatically. After a full page refresh, choose
  <strong>Resume investigation</strong> if you are prompted, or use <strong>Rejoin last case</strong> from the lobby.
  </p>
  <p>
  Your partner may look idle for a moment while you reconnect — that is normal.
  A case is only closed for good when everyone has been away for at least <strong>five minutes</strong> with no one in the game.
  </p>

  <h2>Opening this Help again</h2>
  <p>
    Use the <strong>Help</strong> link on the left HUD, press <kbd>F1</kbd>, or press <kbd>?</kbd> (same key as Shift + <kbd>/</kbd> on many keyboards).
    Press <kbd>Escape</kbd> to close when you are finished.
  </p>

  <p class="manual-footer">
    Have fun out there — and thank you for spending your evening with Cold Case Solvers.
  </p>
</article>
`;
}
